export class ApiError extends Error {
  constructor(public status: number, public code: string, message?: string) {
    super(message ?? code);
  }
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData)) headers.set("content-type", "application/json");
  const response = await fetch(path, { ...options, headers, credentials: "same-origin" });
  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try { body = JSON.parse(text); } catch { body = text; }
  }
  if (!response.ok) {
    const data = typeof body === "object" && body !== null ? body as Record<string, unknown> : {};
    throw new ApiError(response.status, String(data.error ?? "REQUEST_FAILED"), typeof data.message === "string" ? data.message : undefined);
  }
  return body as T;
}

export async function saveMediaToPhotos(url: string, filename: string, mimeType: string): Promise<"shared" | "downloaded"> {
  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) {
    let code = "MEDIA_DOWNLOAD_FAILED";
    try {
      const body = await response.clone().json() as { error?: unknown };
      if (typeof body.error === "string" && body.error) code = body.error;
    } catch {
      // Media endpoints normally return binary data; keep the generic code when no JSON error is available.
    }
    throw new Error(code);
  }
  const blob = await response.blob();
  const file = new File([blob], filename, { type: mimeType });
  const shareData: ShareData = { files: [file], title: filename };
  const iosLike = /iPad|iPhone|iPod/i.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (iosLike && typeof navigator.share === "function" && (typeof navigator.canShare !== "function" || navigator.canShare(shareData))) {
    try {
      await navigator.share(shareData);
      return "shared";
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      // Fall through to a normal browser download if the share sheet is unavailable or rejects the file.
    }
  }
  const objectUrl = URL.createObjectURL(file);
  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
  return "downloaded";
}

export function formatMoney(value: number): string {
  return new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND", maximumFractionDigits: 0 }).format(value);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("vi-VN", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

export interface Product {
  id: string;
  slug: string;
  name: string;
  shortDescription: string;
  description: string;
  logo: string | null;
  category: string;
  regularPrice: number;
  salePrice: number | null;
  price: number;
  stock: number;
  maxQuantity: number;
  warranty: string;
  instructions: string;
  badge: string | null;
  featured: boolean;
  soldCount: number;
  guideUrl?: string | null;
}

export interface GuideBlock {
  id: string;
  type: "TEXT" | "IMAGE" | "VIDEO" | "LINK";
  title: string;
  text: string;
  url: string;
  caption: string;
}

export interface Guide {
  id: string;
  productId: string;
  slug: string;
  title: string;
  summary: string;
  blocks: GuideBlock[];
  updatedAt: string;
  product: { name: string; slug: string; logo: string | null };
}

export interface KnowledgeBlock {
  id: string;
  type: "TEXT" | "IMAGE" | "VIDEO" | "LINK" | "FILE";
  title: string;
  text: string;
  url: string;
  caption: string;
}

export interface KnowledgeArticle {
  id: string;
  slug: string;
  title: string;
  summary: string;
  category: string;
  coverImage: string | null;
  blocks: KnowledgeBlock[];
  featured: boolean;
  views: number;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  email: string;
  email_verified: number;
  hasPassword?: boolean;
  username?: string | null;
  balance?: number;
  role: "CUSTOMER" | "CTV" | "STAFF" | "ADMIN";
  ctv_status: "NONE" | "PENDING" | "ACTIVE" | "BLOCKED";
  name: string | null;
  avatar: string | null;
}

export interface Storefront {
  products: Product[];
  settings: Record<string, string>;
  announcements: Array<Record<string, string | null>>;
}

export interface CreatedOrder {
  orderCode: string;
  publicToken: string;
  paymentReference: string;
  amount: number;
  quantity?: number;
  expiresAt: string;
  paymentRequired: boolean;
  status: string;
  paymentStatus: string;
  qrUrl: string | null;
  bank: Record<string, string> | null;
}

